import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Shield, Lock, ArrowLeft, CheckCircle2, Zap, CreditCard, ArrowRight, Smartphone } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import CinematicBackdrop from '../cinematic/CinematicBackdrop';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lazy-load Razorpay Checkout.js exactly once
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function CheckoutPage() {
  const { user, loading: authLoading, checkAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const packageId = searchParams.get('package') || 'starter';
  const isSuccess = searchParams.get('success') === 'true';
  const sessionId = searchParams.get('session_id');

  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [success, setSuccess] = useState(false);
  const [creditsAdded, setCreditsAdded] = useState(0);

  // Payment method tabs
  const [method, setMethod] = useState('card');     // 'card' | 'upi'
  const [razorpayConfig, setRazorpayConfig] = useState(null);
  const [upiPaying, setUpiPaying] = useState(false);

  // Fetch Razorpay availability once
  useEffect(() => {
    axios.get(`${API}/payments/razorpay/config`)
      .then((r) => setRazorpayConfig(r.data))
      .catch(() => setRazorpayConfig({ enabled: false }));
  }, []);

  // Fetch package info
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/pricing/packages`);
        const found = res.data.find(p => p.id === packageId);
        setPkg(found || res.data[0]);
      } catch { /* non-critical */ }
      finally { setLoading(false); }
    })();
  }, [packageId]);

  // Handle return from Stripe payment
  useEffect(() => {
    if (!isSuccess || !sessionId || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
        if (cancelled) return;
        if (res.data.payment_status === 'paid') {
          const credits = pkg?.credits || 0;
          setCreditsAdded(credits);
          setSuccess(true);
          checkAuth();
        }
      } catch { /* will retry */ }
    })();
    return () => { cancelled = true; };
  }, [isSuccess, sessionId, user, pkg, checkAuth]);

  useEffect(() => {
    if (!authLoading && !user) navigate('/login');
  }, [authLoading, user, navigate]);

  // -- Stripe (Card) --
  const handlePay = async () => {
    setPaying(true);
    try {
      const res = await axios.post(`${API}/payments/create-checkout`, {
        package_id: packageId,
        origin_url: window.location.origin,
      }, { withCredentials: true });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment failed');
      setPaying(false);
    }
  };

  // -- Razorpay (UPI, automated) --
  const handleUpiPay = async () => {
    if (!razorpayConfig?.enabled) {
      toast.error('UPI payments are temporarily unavailable. Please try Card.');
      return;
    }
    setUpiPaying(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok || !window.Razorpay) {
        toast.error('Could not load payment SDK. Check your internet connection.');
        setUpiPaying(false);
        return;
      }

      // 1) Create order on our backend
      const orderRes = await axios.post(
        `${API}/payments/razorpay/create-order`,
        { package_id: packageId },
        { withCredentials: true }
      );
      const order = orderRes.data;

      // 2) Open Razorpay Checkout — UPI only
      const options = {
        key: order.key_id,
        amount: order.amount_paise,
        currency: order.currency,
        order_id: order.order_id,
        name: 'DIS2ORT',
        description: `${order.package_name} — ${order.credits} credits`,
        image: '/favicon.svg',
        prefill: {
          name: order.prefill?.name || user?.name || '',
          email: order.prefill?.email || user?.email || '',
        },
        notes: { package_id: packageId },
        theme: { color: '#FF6B4A' },
        method: { upi: true, card: false, netbanking: false, wallet: false, emi: false, paylater: false },
        modal: {
          ondismiss: () => setUpiPaying(false),
        },
        handler: async (response) => {
          // 3) Verify signature on backend → credits granted
          try {
            const verifyRes = await axios.post(
              `${API}/payments/razorpay/verify`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
              { withCredentials: true }
            );
            if (verifyRes.data?.status === 'paid' || verifyRes.data?.status === 'already_paid') {
              setCreditsAdded(verifyRes.data?.credits_added || order.credits);
              setSuccess(true);
              checkAuth();
            } else {
              toast.error('Payment received but verification failed. Contact support.');
            }
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Verification failed');
          } finally {
            setUpiPaying(false);
          }
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        toast.error(resp?.error?.description || 'UPI payment failed');
        setUpiPaying(false);
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start UPI payment');
      setUpiPaying(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0F] text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#FF6B4A] animate-spin" />
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-[#0A0A0F] text-white">
        <Navbar />
        <div className="max-w-md mx-auto px-6 pt-32 text-center" data-testid="payment-success">
          <div className="w-20 h-20 rounded-full bg-[#34B76F]/15 flex items-center justify-center mx-auto mb-6 animate-fade-up">
            <CheckCircle2 className="w-10 h-10 text-[#34B76F]" />
          </div>
          <h1 className="font-display text-3xl font-bold text-white mb-2">Payment Successful!</h1>
          <p className="text-sm text-white/50 mb-2">Credits have been added to your account.</p>
          <div className="flex items-center justify-center gap-2 mb-10">
            <Zap className="w-5 h-5 text-[#FF6B4A]" />
            <span className="font-display text-2xl font-bold text-[#FF6B4A] italic">+{creditsAdded || pkg?.credits} credits</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/dashboard">
              <Button className="bg-gradient-to-r from-[#FF6B4A] to-[#E040A0] text-white rounded-full font-semibold px-8 text-sm hover:from-[#FF8E72] hover:to-[#FF6B9D]" data-testid="goto-dashboard">
                Go to Dashboard
              </Button>
            </Link>
            <Link to="/dashboard/submit">
              <Button variant="outline" className="border-white/[0.08] text-white rounded-full px-8 text-sm hover:bg-white/[0.04]" data-testid="start-rendering">
                Start Rendering
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!pkg) {
    return (
      <div className="min-h-screen bg-[#0A0A0F] text-white flex items-center justify-center">
        <p className="text-white/50">Package not found</p>
      </div>
    );
  }

  const amountInr = razorpayConfig?.exchange_rate
    ? (pkg.price * razorpayConfig.exchange_rate).toFixed(2)
    : null;

  return (
    <div className="min-h-screen text-white cinematic-grain relative">
      <CinematicBackdrop intensity="soft" />
      <Navbar />

      <div className="max-w-5xl mx-auto px-6 sm:px-8 py-12 relative z-10">
        <Link to="/pricing" className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white transition-colors mb-8" data-testid="back-to-pricing">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to pricing
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left — Order Summary */}
          <div>
            <h1 className="font-display text-3xl font-bold text-white mb-8" data-testid="checkout-heading">Checkout</h1>

            <div className="p-8 rounded-2xl bg-white/[0.03] border border-white/[0.05] mb-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-semibold text-lg text-white">{pkg.name}</h2>
                  <p className="text-xs text-white/50 mt-0.5">GPU Rendering Credits</p>
                </div>
                <div className="font-display text-3xl font-bold text-white">${pkg.price.toFixed(2)}</div>
              </div>

              <div className="space-y-3 border-t border-white/[0.05] pt-5">
                {pkg.features.map((feat) => (
                  <div key={feat} className="flex items-center gap-2.5 text-xs">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#34B76F] shrink-0" />
                    <span className="text-white/70">{feat}</span>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-5 border-t border-white/[0.05] flex items-center justify-between">
                <span className="text-sm font-medium text-white">Total</span>
                <span className="font-display text-2xl font-bold text-[#FF6B4A]">${pkg.price.toFixed(2)} USD</span>
              </div>
            </div>

            <div className="p-5 rounded-xl bg-white/[0.04] border border-[#FF6B4A]/10">
              <p className="text-xs text-white/70 leading-relaxed">
                Credits are added instantly after payment. Use them across all rendering services — AI images, 3D renders, data visualization, and video encoding.
              </p>
            </div>
          </div>

          {/* Right — Payment */}
          <div>
            <h2 className="font-semibold text-lg text-white mb-6">Payment</h2>

            {/* Method tabs */}
            <div className="grid grid-cols-2 gap-2 mb-4" data-testid="payment-method-tabs">
              <button
                onClick={() => setMethod('card')}
                data-testid="method-card"
                className={`p-3 rounded-xl border text-left transition-colors ${method === 'card' ? 'bg-[#4A7CFF]/10 border-[#4A7CFF]/40' : 'bg-white/[0.04] border-white/[0.06] hover:border-white/[0.12]'}`}>
                <CreditCard className={`w-4 h-4 mb-1.5 ${method === 'card' ? 'text-[#4A7CFF]' : 'text-white/50'}`} />
                <div className={`text-xs font-bold uppercase tracking-wider ${method === 'card' ? 'text-white' : 'text-white/60'}`}>Card · Stripe</div>
                <div className="text-[10px] text-white/40 mt-0.5">Instant · Worldwide</div>
              </button>
              <button
                onClick={() => setMethod('upi')}
                data-testid="method-upi"
                disabled={!razorpayConfig?.enabled}
                className={`p-3 rounded-xl border text-left transition-colors ${method === 'upi' ? 'bg-[#FF6B4A]/10 border-[#FF6B4A]/40' : 'bg-white/[0.04] border-white/[0.06] hover:border-white/[0.12]'} ${!razorpayConfig?.enabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <Smartphone className={`w-4 h-4 mb-1.5 ${method === 'upi' ? 'text-[#FF6B4A]' : 'text-white/50'}`} />
                <div className={`text-xs font-bold uppercase tracking-wider ${method === 'upi' ? 'text-white' : 'text-white/60'}`}>UPI · India</div>
                <div className="text-[10px] text-white/40 mt-0.5">PhonePe · GPay · Paytm</div>
              </button>
            </div>

            {method === 'card' && (
              <div className="p-8 rounded-2xl bg-white/[0.03] border border-white/[0.05] space-y-6" data-testid="card-panel">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-white/[0.04]">
                  <CreditCard className="w-8 h-8 text-[#4A7CFF]" />
                  <div>
                    <p className="text-sm font-semibold text-white">Secure Card Payment</p>
                    <p className="text-xs text-white/50">Powered by Stripe. All major cards accepted.</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 px-2">
                  {['Visa', 'Mastercard', 'Amex', 'Discover'].map((card) => (
                    <div key={card} className="px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.05] text-[10px] font-semibold text-white/70">{card}</div>
                  ))}
                </div>
                <Button
                  onClick={handlePay}
                  disabled={paying}
                  className="w-full bg-gradient-to-r from-[#FF6B4A] to-[#E040A0] text-white rounded-xl py-6 font-semibold text-sm hover:from-[#FF8E72] hover:to-[#FF6B9D] disabled:opacity-50 shadow-lg hover:shadow-xl transition-shadow"
                  data-testid="pay-btn"
                >
                  {paying ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Redirecting to payment...</>
                  ) : (
                    <><Lock className="w-4 h-4 mr-2" /> Pay ${pkg.price.toFixed(2)} <ArrowRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>
                <div className="flex items-center justify-center gap-6 text-[10px] text-white/50">
                  <div className="flex items-center gap-1"><Lock className="w-3 h-3" /> SSL Encrypted</div>
                  <div className="flex items-center gap-1"><Shield className="w-3 h-3" /> PCI Compliant</div>
                </div>
                <p className="text-[10px] text-center text-white/50">
                  You&apos;ll be securely redirected to Stripe. Your card details never touch our servers.
                </p>
              </div>
            )}

            {method === 'upi' && (
              <div className="p-8 rounded-2xl bg-white/[0.03] border border-white/[0.05] space-y-6" data-testid="upi-panel">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-[#FF6B4A]/[0.06] border border-[#FF6B4A]/15">
                  <Smartphone className="w-8 h-8 text-[#FF6B4A]" />
                  <div>
                    <p className="text-sm font-semibold text-white">Instant UPI Payment</p>
                    <p className="text-xs text-white/50">Powered by Razorpay · GPay · PhonePe · Paytm · BHIM</p>
                  </div>
                </div>

                {/* INR conversion */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-white/[0.04] border border-white/[0.06]">
                  <div>
                    <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-white/50">You pay</div>
                    <div className="font-display text-3xl font-black text-white mt-0.5">
                      ₹{amountInr ?? '—'}
                    </div>
                  </div>
                  <div className="text-right text-[10px] text-white/40 tracking-wider">
                    <div>= ${pkg.price.toFixed(2)} USD</div>
                    {razorpayConfig?.exchange_rate && <div>@ ₹{razorpayConfig.exchange_rate}/USD</div>}
                  </div>
                </div>

                <div className="flex items-center gap-3 px-2 flex-wrap">
                  {['GPay', 'PhonePe', 'Paytm', 'BHIM'].map((app) => (
                    <div key={app} className="px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.05] text-[10px] font-semibold text-white/70">{app}</div>
                  ))}
                </div>

                <Button
                  onClick={handleUpiPay}
                  disabled={upiPaying || !razorpayConfig?.enabled}
                  className="w-full bg-gradient-to-r from-[#FF6B4A] to-[#E040A0] text-white rounded-xl py-6 font-semibold text-sm hover:from-[#FF8E72] hover:to-[#FF6B9D] disabled:opacity-50 shadow-lg hover:shadow-xl transition-shadow"
                  data-testid="upi-pay-btn"
                >
                  {upiPaying ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Opening UPI...</>
                  ) : (
                    <><Smartphone className="w-4 h-4 mr-2" /> Pay ₹{amountInr ?? '...'} via UPI <ArrowRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>

                <div className="flex items-center justify-center gap-6 text-[10px] text-white/50">
                  <div className="flex items-center gap-1"><Lock className="w-3 h-3" /> 256-bit Secure</div>
                  <div className="flex items-center gap-1"><Shield className="w-3 h-3" /> Razorpay Verified</div>
                </div>
                <p className="text-[10px] text-center text-white/50">
                  Credits added instantly after UPI confirmation. No manual approval needed.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
